import React from 'react';
import { Link } from 'react-router-dom';
import Spinner from 'components/V1/Spinner/Spinner';
import {
  styled,
  v2Durations,
  v2FontSizes,
  v2FontWeights,
  v2Gradients,
  v2Radii,
  v2Shadows,
  v2Sizes,
} from 'theme';

const StyledAuthButton = styled('button', {
  // Explicit rather than left to the <button> defaults, so the same styles
  // lay out an <a> (LinkedAuthButton) as a full-width, centred block too.
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  textDecoration: 'none',
  width: '100%',
  height: v2Sizes.button,
  border: 0,
  borderRadius: v2Radii.control,
  color: '$white',
  fontSize: v2FontSizes.button,
  fontWeight: v2FontWeights.heavy,
  letterSpacing: '$tighter',
  cursor: 'pointer',

  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  backgroundColor: '$violet900',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    backgroundImage: v2Gradients.brand,
    opacity: 0,
    transition: `opacity ${v2Durations.base}`,
  },
  '&:hover:not(:disabled)::before, &:active:not(:disabled)::before': {
    opacity: 1,
  },
  boxShadow: v2Shadows.button,
  transition: `transform ${v2Durations.fast}, box-shadow ${v2Durations.base}`,
  '&:hover:not(:disabled)': {
    boxShadow: v2Shadows.buttonHover,
  },
  '&:active:not(:disabled)': { transform: 'translateY(1px)' },
  '&:focus-visible': { boxShadow: '$focus' },
  '&:disabled': { opacity: 0.7, cursor: 'not-allowed' },
});

interface AuthButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  readonly isLoading?: boolean;
}

function AuthButton({ children, isLoading, ...rest }: AuthButtonProps) {
  return (
    <StyledAuthButton {...rest} disabled={isLoading || rest.disabled}>
      {isLoading ? <Spinner /> : children}
    </StyledAuthButton>
  );
}

export default AuthButton;

export const LinkedAuthButton = styled(Link, StyledAuthButton);
